import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { httpinstance } from "../../axios/api";

/**
 * ============================================================================
 * Auth state used to be bootstrapped synchronously from `localStorage`
 * (accessToken/user) and every login/OTP-verify thunk wrote the raw token
 * and full profile back into it. That token is exactly what an XSS payload
 * would go looking for (Task #99), so it no longer touches browser
 * storage at all — the access/refresh tokens live only in httpOnly cookies
 * the backend sets, and the CSRF token in a cookie axios reads directly
 * (src/axios/api.js).
 *
 * Practical effect: `logedUser` can no longer be seeded synchronously on
 * page load from a cached blob. `checkSession` (GET /auth/session) confirms
 * the cookie session is still genuinely active server-side and reseeds
 * `logedUser` after a reload — call it once on app/protected-route mount
 * (see routes/ProtectedRoute.jsx).
 *
 * `checkSession` returns the same sanitized full-profile shape as
 * login/OTP-verify (the user document minus password/OTP/remember-token
 * internals — see backend utils/userProfile.js), not just a handful of
 * fields, so `logedUser`/`authuser` is a stable single source of truth for
 * profile reads (role, KYC status, customer category, branch, etc.) across
 * both a fresh login and any later reload. Prefer `useSelector(authuser)`
 * over reading `localStorage` directly — nothing populates a `user` key in
 * browser storage anymore.
 * ============================================================================
 */

export const userlogin = createAsyncThunk("userlogin", async (values) => {
  try {
    const url = "auth/login";
    const response = await httpinstance.post(url, values);
    return response;
  } catch (err) {
    return err.response;
  }
});

/**
 * OTP request for the Email / SMS / WhatsApp login flow (Task #94).
 * For the phone channels `destination` is the national number and
 * `country_code` its dial code — phone numbers are stored split, so the
 * lookup has to be made with both halves.
 */
export const requestLoginOtp = createAsyncThunk("requestLoginOtp", async ({ channel, destination, country_code }) => {
  try {
    const response = await httpinstance.post("auth/otp/request", { channel, destination, country_code });
    return response;
  } catch (err) {
    return err.response;
  }
});

export const verifyLoginOtp = createAsyncThunk("verifyLoginOtp", async ({ channel, destination, country_code, otp }) => {
  try {
    const response = await httpinstance.post("auth/otp/verify", { channel, destination, country_code, otp });
    return response;
  } catch (err) {
    return err.response;
  }
});

/**
 * Email verification for a logged-in user (VerifyEmailModal). All three take
 * the user's own `userId` beside the email. Resend invalidates the previous
 * code; verify expects `otp` encrypted with the email as the key source
 * (see encryptEmailOtp in utils/clientCrypto.js).
 */
export const requestEmailVerifyOtp = createAsyncThunk("requestEmailVerifyOtp", async ({ email, userId }) => {
  try {
    const response = await httpinstance.post("auth/email/otp/request", { email, userId });
    return response;
  } catch (err) {
    return err.response;
  }
});

export const resendEmailVerifyOtp = createAsyncThunk("resendEmailVerifyOtp", async ({ email, userId }) => {
  try {
    const response = await httpinstance.post("auth/email/otp/resend", { email, userId });
    return response;
  } catch (err) {
    return err.response;
  }
});

export const verifyEmailOtp = createAsyncThunk("verifyEmailOtp", async ({ email, userId, otp }) => {
  try {
    const response = await httpinstance.post("auth/email/otp/verify", { email, userId, otp });
    return response;
  } catch (err) {
    return err.response;
  }
});

/** Confirms the server-side session is genuinely active (Task #100). */
export const checkSession = createAsyncThunk("checkSession", async () => {
  try {
    const response = await httpinstance.get("auth/session");
    return response;
  } catch (err) {
    return err.response;
  }
});

export const forgotpassword = createAsyncThunk("forgotpassword", async (values) => {
  try {
    const url = "forgotpassword";
    const response = await httpinstance.post(url, values);
    return response;
  } catch (err) {
    return err.response;
  }
});
export const setpassword = createAsyncThunk("setpassword", async (values) => {
  try {
    const url = "auth/passwordreset";
    const response = await httpinstance.post(url, values);
    return response;
  } catch (err) {
    return err.response;
  }
});

/** Ends the current session only (Task #101). Idempotent server-side;
 * client state is always cleared even if the network call fails. */
export const logout = createAsyncThunk("logout", async () => {
  try {
    const response = await httpinstance.post("auth/sessions/logout");
    return response;
  } catch (err) {
    return err.response;
  } finally {
    // Client-side cleanup is not the security boundary (the server-side
    // revocation above is) but it must happen unconditionally so the UI
    // reflects "logged out" even if the request itself failed offline.
  }
});

/** Ends every active session for the account (Task #101). */
export const logoutEverywhere = createAsyncThunk("logoutEverywhere", async () => {
  try {
    const response = await httpinstance.post("auth/sessions/logout-all");
    return response;
  } catch (err) {
    return err.response;
  }
});

export const checkpasseordresetlink = createAsyncThunk("checkpasseordresetlink", async (values) => {
  try {
    const url = "check_reset_password";
    const response = await httpinstance.post(url, values);
    return response;
  } catch (err) {
    return err.response;
  }
});
export const singleSignonService = createAsyncThunk("singleSignonService", async () => {
  try {
    const url = "auth/single-sign-on";
    const response = await httpinstance.post(url);
    return response;
  } catch (err) {
    return err.response;
  }
});

const initialState = {
  logedUser: {},
  sessionChecked: false,
  loading: false,
  message: "",
};

const loginSlice = createSlice({
  name: "loginslice",
  initialState,
  reducers: {
    clearLoggedInUser: (state) => {
      state.logedUser = {};
      state.sessionChecked = true;
    },
    // Merges a partial profile update (e.g. the fields returned by
    // PUT /edit_profile) into the session's cached user object. Replaces the
    // old pattern of merging into a `localStorage` "user" blob — that blob
    // is never populated post-hardening, so a plain overwrite there silently
    // discarded every other known field (userroles, kyc_verification_status,
    // etc.) on every profile save (see store/User/ProfileSlice.js).
    mergeLoggedInUser: (state, { payload }) => {
      state.logedUser = { ...state.logedUser, ...(payload && typeof payload === "object" ? payload : {}) };
    },
  },
  extraReducers: {
    [userlogin.pending]: (state) => {
      state.loading = true;
    },
    [userlogin.fulfilled]: (state, { payload }) => {
      return {
        ...state,
        loading: false,
        sessionChecked: true,
        logedUser: payload?.data?.data?.original?.user || state.logedUser,
      };
    },
    [userlogin.rejected]: (state, action) => {
      state.loading = false;
      state.message = action.payload;
    },
    [verifyLoginOtp.fulfilled]: (state, { payload }) => {
      return {
        ...state,
        sessionChecked: true,
        logedUser: payload?.data?.data || state.logedUser,
      };
    },
    [checkSession.fulfilled]: (state, { payload }) => {
      return {
        ...state,
        sessionChecked: true,
        logedUser: payload?.data?.data?.user || {},
      };
    },
    [checkSession.rejected]: (state) => {
      state.sessionChecked = true;
      state.logedUser = {};
    },
    [logout.fulfilled]: (state) => {
      state.logedUser = {};
      state.sessionChecked = true;
    },
    [logout.rejected]: (state) => {
      // Logout must "succeed" from the user's perspective even when the
      // network call itself failed (Task #101) — clear local state
      // regardless of the outcome.
      state.logedUser = {};
      state.sessionChecked = true;
    },
    [logoutEverywhere.fulfilled]: (state) => {
      state.logedUser = {};
      state.sessionChecked = true;
    },
  },
});

export const { clearLoggedInUser, mergeLoggedInUser } = loginSlice.actions;
export const authuser = (state) => state.userlogin.logedUser;
export const authtoken = () => null; // No token is ever exposed to JS anymore — see module header.
export const isSessionChecked = (state) => state.userlogin.sessionChecked;

export default loginSlice.reducer;
